import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebsiteSettings } from '../../context/WebsiteSettingsContext';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

interface Section { tag: string; tag_bn?: string; title: string; title_bn?: string; content: string; content_bn?: string; }

interface TermsData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  last_updated: string;
  sections: Section[];
}

const TermsConditions: React.FC = () => {
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const { settings } = useWebsiteSettings();
  const siteName = settings?.site_name || 'Our Store';
  const [cms, setCms] = useState<TermsData | null>(null);

  useEffect(() => {
    fetch(`${API}/pages/terms/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCms(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const hero_title = t(cms?.hero_title, cms?.hero_title_bn, 'Terms & Conditions');
  const hero_subtitle = t(cms?.hero_subtitle, cms?.hero_subtitle_bn, 'Please read these terms carefully before using our platform.');
  const last_updated = cms?.last_updated || 'January 2025';
  const sections = cms?.sections || [
    { tag: '1. Agreement', title: 'Acceptance of Terms', content: `By accessing or using ${siteName}, you agree to be bound by these Terms and Conditions and our Privacy Policy.` },
    { tag: '2. Eligibility', title: 'Who Can Use Our Platform', content: `You must be at least 18 years of age to create an account and place orders on our platform.` },
  ];

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        <div className="static-section">
          <div className="policy-callout">
            <strong>{isBn ? '\u09B8\u09B0\u09CD\u09AC\u09B6\u09C7\u09B7 \u0986\u09AA\u09A1\u09C7\u099F:' : 'Last Updated:'}</strong> {last_updated} &nbsp;|&nbsp;
            {isBn
              ? `${siteName} \u09AC\u09CD\u09AF\u09AC\u09B9\u09BE\u09B0 \u0995\u09B0\u09C7, \u0986\u09AA\u09A8\u09BF \u09A8\u09BF\u09AE\u09CD\u09A8\u09B2\u09BF\u0996\u09BF\u09A4 \u09B6\u09B0\u09CD\u09A4\u09BE\u09AC\u09B2\u09C0\u09A4\u09C7 \u09B8\u09AE\u09CD\u09AE\u09A4 \u09B9\u099A\u09CD\u099B\u09C7\u09A8\u0964`
              : `By using ${siteName}, you agree to the following terms and conditions.`}
          </div>
        </div>

        {sections.map((sec, i) => (
          <React.Fragment key={i}>
            {i > 0 && <hr className="static-divider" />}
            <div className="static-section">
              <span className="section-tag">{isBn ? (sec.tag_bn || sec.tag) : sec.tag}</span>
              <h2>{isBn ? (sec.title_bn || sec.title) : sec.title}</h2>
              {(isBn ? (sec.content_bn || sec.content) : sec.content).split('\n').filter(Boolean).map((p: string, pi: number) => (
                <p key={pi} style={pi > 0 ? { marginTop: 12 } : undefined}>{p}</p>
              ))}
            </div>
          </React.Fragment>
        ))}

        <hr className="static-divider" />

        <div className="static-section">
          <span className="section-tag">{sections.length + 1}. {isBn ? '\u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997' : 'Contact'}</span>
          <h2>{isBn ? '\u098F\u0987 \u09B6\u09B0\u09CD\u09A4\u09BE\u09AC\u09B2\u09C0 \u09B8\u09AE\u09CD\u09AA\u09B0\u09CD\u0995\u09C7 \u09AA\u09CD\u09B0\u09B6\u09CD\u09A8 \u0986\u099B\u09C7?' : 'Questions About These Terms?'}</h2>
          <div className="static-card">
            <h3>{isBn ? '\u0986\u09AE\u09B0\u09BE \u09B8\u09BE\u09B9\u09BE\u09AF\u09CD\u09AF \u0995\u09B0\u09A4\u09C7 \u098F\u0996\u09BE\u09A8\u09C7' : 'We\'re Here to Help'}</h3>
            <p>
              {isBn
                ? `\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u09B6\u09B0\u09CD\u09A4\u09BE\u09AC\u09B2\u09C0 \u09B8\u09AE\u09CD\u09AA\u09B0\u09CD\u0995\u09C7 \u0995\u09CB\u09A8\u09CB \u09AA\u09CD\u09B0\u09B6\u09CD\u09A8 \u09A5\u09BE\u0995\u09B2\u09C7, \u09AF\u09CB\u0997\u09BE\u09AF\u09CB\u0997 \u0995\u09B0\u09C1\u09A8: `
                : 'If you have any questions about our Terms & Conditions, please contact us at '}
              <a href={`mailto:support@${siteName.toLowerCase().replace(/\s+/g, '')}.com`} style={{ color: 'var(--primary)' }}>
                support@{siteName.toLowerCase().replace(/\s+/g, '')}.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
